import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Pencil, Trash2, Save, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ListingForm {
  address: string;
  neighborhood: string;
  monthly_rent: string;
  square_footage: string;
  price_per_sqft: string;
  space_type: "second_gen" | "new";
  zoning: string;
  available_date: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  crexi_url: string;
  image_url: string;
  city: string;
  is_active: boolean;
}

const EMPTY_FORM: ListingForm = {
  address: "",
  neighborhood: "",
  monthly_rent: "",
  square_footage: "",
  price_per_sqft: "",
  space_type: "second_gen",
  zoning: "",
  available_date: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  crexi_url: "",
  image_url: "",
  city: "Miami",
  is_active: true,
};

interface Listing {
  id: string;
  address: string;
  neighborhood: string | null;
  monthly_rent: number | null;
  square_footage: number | null;
  price_per_sqft: number | null;
  space_type: "second_gen" | "new";
  zoning: string | null;
  available_date: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  crexi_url: string | null;
  image_url: string | null;
  city: string;
  is_active: boolean;
  last_updated: string;
  created_at: string;
  user_id: string;
}

export default function AdminListings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ListingForm>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchListings();
  }, []);

  async function fetchListings() {
    setLoading(true);
    const { data, error } = await supabase
      .from("restaurant_listings")
      .select("*")
      .order("last_updated", { ascending: false });

    if (error) {
      toast.error("Failed to load listings");
    } else {
      setListings((data as Listing[]) || []);
    }
    setLoading(false);
  }

  function startEdit(listing: Listing) {
    setForm({
      address: listing.address,
      neighborhood: listing.neighborhood || "",
      monthly_rent: listing.monthly_rent?.toString() || "",
      square_footage: listing.square_footage?.toString() || "",
      price_per_sqft: listing.price_per_sqft?.toString() || "",
      space_type: listing.space_type,
      zoning: listing.zoning || "",
      available_date: listing.available_date || "",
      contact_name: listing.contact_name || "",
      contact_email: listing.contact_email || "",
      contact_phone: listing.contact_phone || "",
      crexi_url: listing.crexi_url || "",
      image_url: listing.image_url || "",
      city: listing.city,
      is_active: listing.is_active,
    });
    setEditingId(listing.id);
    setShowForm(true);
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  }

  async function handleSave() {
    if (!form.address.trim()) {
      toast.error("Address is required");
      return;
    }
    if (!user) {
      toast.error("You must be logged in");
      return;
    }

    setSaving(true);
    const payload = {
      address: form.address,
      neighborhood: form.neighborhood || null,
      monthly_rent: form.monthly_rent ? parseInt(form.monthly_rent) : null,
      square_footage: form.square_footage ? parseInt(form.square_footage) : null,
      price_per_sqft: form.price_per_sqft ? parseFloat(form.price_per_sqft) : null,
      space_type: form.space_type as "second_gen" | "new",
      zoning: form.zoning || null,
      available_date: form.available_date || null,
      contact_name: form.contact_name || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      crexi_url: form.crexi_url || null,
      image_url: form.image_url || null,
      city: form.city,
      is_active: form.is_active,
    };

    if (editingId) {
      const { error } = await supabase
        .from("restaurant_listings")
        .update(payload)
        .eq("id", editingId);
      if (error) {
        toast.error("Failed to update listing");
      } else {
        toast.success("Listing updated");
        resetForm();
        fetchListings();
      }
    } else {
      const { error } = await supabase
        .from("restaurant_listings")
        .insert({ ...payload, user_id: user.id });
      if (error) {
        toast.error("Failed to create listing");
      } else {
        toast.success("Listing created");
        resetForm();
        fetchListings();
      }
    }
    setSaving(false);
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this listing?")) return;
    const { error } = await supabase
      .from("restaurant_listings")
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Failed to delete");
    } else {
      toast.success("Listing deleted");
      fetchListings();
    }
  }

  function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-medium text-foreground">Admin — Listings</h1>
            <p className="text-sm text-muted-foreground">Manage restaurant space listings</p>
          </div>
          {!showForm && (
            <Button onClick={() => { resetForm(); setShowForm(true); }} size="sm">
              <Plus className="w-4 h-4 mr-1" /> Add Listing
            </Button>
          )}
        </div>
      </div>

      <div className="container pt-6 max-w-4xl">
        {/* Form */}
        {showForm && (
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-medium text-foreground">
                {editingId ? "Edit Listing" : "New Listing"}
              </h2>
              <Button variant="ghost" size="icon" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Address *">
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St" />
              </Field>
              <Field label="City">
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Miami" />
              </Field>
              <Field label="Neighborhood">
                <Input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} placeholder="Brickell" />
              </Field>
              <Field label="Monthly Rent ($)">
                <Input type="number" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} placeholder="5000" />
              </Field>
              <Field label="Square Footage">
                <Input type="number" value={form.square_footage} onChange={(e) => setForm({ ...form, square_footage: e.target.value })} placeholder="1500" />
              </Field>
              <Field label="Price per Sqft ($/yr)">
                <Input type="number" step="0.01" value={form.price_per_sqft} onChange={(e) => setForm({ ...form, price_per_sqft: e.target.value })} placeholder="45.00" />
              </Field>
              <Field label="Space Type">
                <Select value={form.space_type} onValueChange={(v) => setForm({ ...form, space_type: v as "second_gen" | "new" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="second_gen">2nd Generation</SelectItem>
                    <SelectItem value="new">New Buildout</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Zoning">
                <Input value={form.zoning} onChange={(e) => setForm({ ...form, zoning: e.target.value })} placeholder="C-1 Commercial" />
              </Field>
              <Field label="Available Date">
                <Input type="date" value={form.available_date} onChange={(e) => setForm({ ...form, available_date: e.target.value })} />
              </Field>
              <Field label="Contact Name">
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Doe" />
              </Field>
              <Field label="Contact Email">
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="john@example.com" />
              </Field>
              <Field label="Contact Phone">
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(305) 555-0123" />
              </Field>
              <Field label="Crexi / Listing URL">
                <Input value={form.crexi_url} onChange={(e) => setForm({ ...form, crexi_url: e.target.value })} placeholder="https://..." />
              </Field>
              <Field label="Image URL">
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
              </Field>
              <Field label="Active">
                <div className="pt-2">
                  <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
                </div>
              </Field>
            </div>

            <div className="flex gap-2 mt-5">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                {editingId ? "Update" : "Create"}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No listings yet. Click "Add Listing" to create one.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Address</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">City</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Rent</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Sqft</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Active</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Updated</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {listings.map((l) => (
                    <tr key={l.id} className="hover:bg-muted/10 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground max-w-[200px] truncate">{l.address}</td>
                      <td className="px-4 py-3 text-muted-foreground">{l.city}</td>
                      <td className="px-4 py-3 text-muted-foreground">{l.monthly_rent ? `$${l.monthly_rent.toLocaleString()}` : "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{l.square_footage?.toLocaleString() || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{l.space_type === "second_gen" ? "2nd Gen" : "New"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block w-2 h-2 rounded-full ${l.is_active ? "bg-primary" : "bg-muted-foreground/30"}`} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(l.last_updated).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(l)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(l.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
